import { useState } from 'react';

import { RateAndReviewModal } from '../rateAndReviewModal/rateAndReviewModal';
import { canGoBack } from '../rateAndReviewModalVariant';
import type { RateAndReviewModalVariantProps } from '../rateAndReviewModalVariant';
import { RateAndReviewPhaseBody } from '../rateAndReviewPhaseBody/rateAndReviewPhaseBody';
import { RateAndReviewWizard } from '../rateAndReviewWizard/rateAndReviewWizard';
import { ReviewProductHeader } from '../reviewProductHeader/reviewProductHeader';

export type RateAndReviewWizardModalProps = RateAndReviewModalVariantProps;

/**
 * A/B test variant B: the modal shell with one question per step.
 * Owns the current step and restarts from the first step whenever a review
 * starts: on open, and when a product is picked from a list.
 */
export function RateAndReviewWizardModal({
  activityOptions,
  brand,
  category,
  errorMessage,
  feelingOptions,
  imageUrl,
  isMobile,
  isRetryable = true,
  isSubmitting,
  moreToReview,
  onBack,
  onChange,
  onRequestClose,
  onReviewProduct,
  onSubmit,
  open,
  productName,
  status = 'editing',
  testId,
  title,
  value,
}: RateAndReviewWizardModalProps) {
  const [step, setStep] = useState(0);
  const isShowingForm = open && status === 'editing';
  const [wasShowingForm, setWasShowingForm] = useState(isShowingForm);

  if (isShowingForm !== wasShowingForm) {
    setWasShowingForm(isShowingForm);
    if (isShowingForm) setStep(0);
  }

  const getBackAction = () => {
    if (!canGoBack({ isRetryable, isSubmitting, status })) return undefined;
    if (status === 'editing' && step > 0) return () => setStep(step - 1);
    return onBack;
  };

  return (
    <RateAndReviewModal
      isMobile={isMobile}
      onBack={getBackAction()}
      onRequestClose={onRequestClose}
      open={open}
      productHeader={
        status === 'editing' && (
          <ReviewProductHeader
            brand={brand}
            category={category}
            imageUrl={imageUrl}
            name={productName}
            size="compact"
          />
        )
      }
      testId={testId}
      title={title}
    >
      <RateAndReviewPhaseBody
        errorMessage={errorMessage}
        form={
          <RateAndReviewWizard
            activityOptions={activityOptions}
            feelingOptions={feelingOptions}
            isSubmitting={isSubmitting}
            onChange={onChange}
            onStepChange={setStep}
            onSubmit={onSubmit}
            step={step}
            value={value}
          />
        }
        isRetryable={isRetryable}
        isSubmitting={isSubmitting}
        moreToReview={moreToReview}
        onRequestClose={onRequestClose}
        onReviewProduct={onReviewProduct}
        onSubmit={onSubmit}
        productName={productName}
        status={status}
        value={value}
      />
    </RateAndReviewModal>
  );
}
